import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { CloseOutlined, FullscreenOutlined, FullscreenExitOutlined, PushpinFilled, PushpinOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useThreadPanelStore } from '../stores';
import { useMobileViewport } from '../hooks/useMobileViewport';
import TaskStatusPill from './TaskStatusPill';
import TaskHeartbeatControl from './TaskHeartbeatControl';
import type { Task } from '../lib/api';
import { ExecutionUsageChip } from './ExecutionUsageChip';

/**
 * The toggle names what it controls and leaves where it currently stands to
 * `aria-pressed`. Naming the action instead — "let the thread float" while
 * pressed, "dock the thread" while not — has a screen reader announce the next
 * placement and the present one in the same breath, which reads as a
 * contradiction.
 */
const PLACEMENT_LABEL = msg`Dock the thread beside the channel`;

/**
 * The header both thread panels wear: the title on the left, and on the right
 * the placement toggle followed by the only control that closes the panel.
 *
 * The toggle is absent on mobile, where the thread takes the whole screen and
 * has no channel to sit beside or float over.
 */
interface Props {
  title: string;
  onClose: () => void;
  task?: Task;
  assigneeName?: string;
  parentChannelRoute?: string;
  conversationId?: string;
  sessionLayoutOwner?: string;
}

export default function ThreadPanelHeader({
  title,
  onClose,
  task,
  assigneeName,
  parentChannelRoute,
  conversationId,
  sessionLayoutOwner,
}: Props) {
  const { t, i18n } = useLingui();
  const isMobile = useMobileViewport();
  const mode = useThreadPanelStore((s) => s.mode);
  const toggleMode = useThreadPanelStore((s) => s.toggleMode);

  const sessionLayout = useThreadPanelStore(s => s.sessionLayout?.owner === sessionLayoutOwner ? s.sessionLayout : null);
  const toggleSessionExpanded = useThreadPanelStore(s => s.toggleSessionExpanded);
  const expanded = sessionLayout?.expanded === true;

  const pinned = mode === 'pinned';

  return (
    <div className="thread-panel-header">
      <div className="thread-panel-heading">
        <span className="thread-panel-title" title={task?.title}>
          {task?.title ?? title}
        </span>
        {task && (
          <div className="thread-panel-task-controls">
            <TaskStatusPill
              task={task}
              assigneeName={assigneeName}
              showUnassigned={!task.assignee_id}
            />
            <TaskHeartbeatControl task={task} />
          </div>
        )}
      </div>
      <div className="thread-panel-actions">
        {parentChannelRoute && (
          <Link
            className="thread-panel-parent-channel"
            to={parentChannelRoute}
            aria-label={t`Go to channel`}
            title={t`Go to channel`}
          >
            <Trans>Go to channel</Trans>
          </Link>
        )}
        {conversationId && <ExecutionUsageChip resourceId={conversationId} scope="thread" />}
        {!isMobile && sessionLayout && (
          <button
            type="button"
            className="thread-panel-placement"
            onClick={() => toggleSessionExpanded(sessionLayout.owner)}
            aria-pressed={expanded}
            aria-label={expanded ? t`Restore session width` : t`Expand session to workspace`}
            title={expanded ? t`Restore session width` : t`Expand session to workspace`}
          >
            {expanded ? <FullscreenExitOutlined aria-hidden="true" /> : <FullscreenOutlined aria-hidden="true" />}
          </button>
        )}
        {!isMobile && (
          <button
            type="button"
            className="thread-panel-placement"
            onClick={toggleMode}
            disabled={expanded}
            aria-pressed={pinned}
            aria-label={i18n._(PLACEMENT_LABEL)}
            title={i18n._(PLACEMENT_LABEL)}
          >
            {pinned ? <PushpinFilled aria-hidden="true" /> : <PushpinOutlined aria-hidden="true" />}
          </button>
        )}
        <button
          type="button"
          className="thread-panel-close"
          onClick={onClose}
          aria-label={t`Close thread`}
          title={t`Close thread`}
        >
          <CloseOutlined aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
